/**
 * P2-05: ResourceRow —— 主资源表行（迁移自 WResourceRow，js/jsx/left.jsx.js:50-288）。
 *
 * 显示条件逐条对齐 INVENTORY（webui/src/panels/left/INVENTORY.md "显示条件细节"）：
 * 1. res.visible || showHiddenResources；
 * 2. res.unlocked || (kittens 且有 maxValue)；
 * 3. !res.isHidden || isEditMode；
 * 4. craftable 迁移规则：isNormalCraftableResource 且配方已解锁 → 挪到合成表。
 *
 * 旧 shouldComponentUpdate 可见性短路（left.jsx.js:59-70）转化为行级
 * useGameTick 精确选择器：伪资源每次 render 重建（INVENTORY 风险点 7），
 * 故本组件按 name 键控、选择器内按字段比较；不可见时返回常量快照
 * （对齐旧"不可见 → 跳过渲染"语义），可见性翻转由快照 shown 字段变化捕获。
 *
 * 全部旧 class 钩子保留（对照 webui/test-baseline/left/ 基线截图）：
 * res-row / resource_<name> / resLeaderBonus / highlited / hidden /
 * res-cell / resource-name / resAmount / resLimitWarn / resLimitNotice /
 * blaze / hail / pray / leet / maxRes / resPerTick / positive-weather / negative-weather。
 *
 * tooltip 在 P2-06 接入；本步 perTick 格为纯文本。
 */
import { useCallback } from "react";
import type { CSSProperties, MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import { TooltipAnchor } from "../../components/Tooltip/TooltipProvider";
import { getResourceTooltipContent } from "./resourceTooltip";

// ---------------------------------------------------------------------------
// 资源解析与可见性（纯函数，导出供测试与 ResourceTable 复用）
// ---------------------------------------------------------------------------

/** 引擎资源对象的最小形状（真实资源与伪资源共用字段） */
export interface ResLike {
  name: string;
  title?: string;
  value: number;
  maxValue?: number;
  unlocked: boolean;
  visible?: boolean;
  isHidden?: boolean;
  type?: string;
  color?: string;
  style?: Record<string, unknown>;
  craftable?: boolean;
  calculatePerDay?: boolean;
  calculateOnYear?: boolean;
}

/**
 * 按 name 解析资源：先查 resPool（真实资源对象原地变异，引用稳定），
 * 未命中再查伪资源（每次重建，调用方须按字段比较，不可按引用）。
 */
export function resolveResource(game: GamePage, name: string): ResLike | null {
  const res = game.resPool.get(name);
  if (res) {
    return res as ResLike;
  }
  const pseudos = game.resPool.getPseudoResources() as ResLike[];
  for (const p of pseudos) {
    if (p.name === name) {
      return p;
    }
  }
  return null;
}

export interface ResVisibilityOpts {
  isEditMode: boolean;
  showHiddenResources: boolean;
}

/** 逐条对齐旧 getIsResInMainTable（left.jsx.js:251-268） */
export function isResInMainTable(
  game: GamePage,
  res: ResLike,
  opts: ResVisibilityOpts,
): boolean {
  if (!res.visible && !opts.showHiddenResources) {
    return false;
  }
  const hasVisibility = res.unlocked || (res.name == "kittens" && res.maxValue);
  if (!hasVisibility || (res.isHidden && !opts.isEditMode)) {
    return false;
  }
  // migrate dual resources (e.g. blueprint) to lower table once craft recipe is unlocked
  if (game.resPool.isNormalCraftableResource(res) && game.workshop.getCraft(res.name).unlocked) {
    return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// 行快照（选择器结果；全部字段按值比较，styleRef 按引用——真实资源 style 引用稳定）
// ---------------------------------------------------------------------------

interface HiddenSnapshot {
  shown: false;
}

interface VisibleSnapshot {
  shown: true;
  /** res.title || res.name */
  title: string;
  /** 行是否带 " hidden" 后缀（!res.visible） */
  rowHidden: boolean;
  /** 名称格行内样式（对齐旧 resNameCss 的逐分支覆盖语义，left.jsx.js:101-122） */
  cssColor?: string;
  cssTextShadow?: string;
  styleRef?: Record<string, unknown>;
  /** 数值格文本与告警 class */
  valueText: string;
  amtClassName: string;
  specialClass: string;
  /** 上限格文本（含前导 "/"，无上限为空串） */
  maxText: string;
  /** 速率格文本（悖论时的 "???" 由渲染层按 prop 决定，对齐旧版） */
  perTickVal: string;
  /** 天气修正文本与 class（无修正为 null） */
  weatherValue: string | null;
  weatherClass: string | null;
  /** " resLeaderBonus " 或 "" */
  leaderBonus: string;
  /** 名称格原生 title 全文 */
  nameTitle: string;
  /** 编辑模式复选框 checked = !isHidden；toggleView 用 */
  isHidden: boolean;
}

export type ResRowSnapshot = HiddenSnapshot | VisibleSnapshot;

function snapshotEq(a: ResRowSnapshot | null, b: ResRowSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b || a.shown !== b.shown) {
    return false;
  }
  if (!a.shown || !b.shown) {
    // 两边均不可见：对齐旧短路语义——不比较任何字段，永不因此重渲染
    return true;
  }
  return (
    a.title === b.title &&
    a.rowHidden === b.rowHidden &&
    a.cssColor === b.cssColor &&
    a.cssTextShadow === b.cssTextShadow &&
    a.styleRef === b.styleRef &&
    a.valueText === b.valueText &&
    a.amtClassName === b.amtClassName &&
    a.specialClass === b.specialClass &&
    a.maxText === b.maxText &&
    a.perTickVal === b.perTickVal &&
    a.weatherValue === b.weatherValue &&
    a.weatherClass === b.weatherClass &&
    a.leaderBonus === b.leaderBonus &&
    a.nameTitle === b.nameTitle &&
    a.isHidden === b.isHidden
  );
}

// ---------------------------------------------------------------------------
// 快照计算（逐行对齐 left.jsx.js:72-268 的 render 逻辑）
// ---------------------------------------------------------------------------

export function computeRowSnapshot(
  game: GamePage | null,
  name: string,
  opts: ResVisibilityOpts,
): ResRowSnapshot | null {
  if (!game) {
    return null;
  }
  const res = resolveResource(game, name);
  if (!res || !isResInMainTable(game, res, opts)) {
    return { shown: false };
  }

  // 速率文本（left.jsx.js:83-97）。注意 perTick 经百分比换算后可能是字符串，
  // 旧版天气判断用的就是这个变换后的值（"0.00" !== 0 为真），此处原样保留。
  let perTick: number | string = game.getResourcePerTick(res.name, true);
  perTick = game.opts.usePerSecondValues ? (perTick as number) * game.getTicksPerSecondUI() : perTick;
  let postfix = game.opts.usePerSecondValues ? "/" + t("unit.s") : "";
  if (game.opts.usePercentageResourceValues && res.maxValue) {
    perTick = ((perTick as number) / (res.maxValue as number) * 100).toFixed(2);
    postfix = "%" + postfix;
  }

  const perTickVal =
    game.getResourcePerTick(res.name, false) || game.getResourcePerTickConvertion(res.name)
      ? game.getDisplayValueExt(perTick, true, false) + postfix
      : res.calculatePerDay
        ? game.getDisplayValueExt(game.getResourcePerDay(res.name), true, false) + "/" + t("unit.d")
        : res.calculateOnYear
          ? game.getDisplayValueExt(game.getResourceOnYearProduction(res.name), true, false) + "/" + t("unit.y")
          : "";

  // 名称颜色（left.jsx.js:101-122）：type → color → style 逐层覆盖/合并
  let cssColor: string | undefined;
  let cssTextShadow: string | undefined;
  if (res.type == "uncommon") {
    cssColor = "Coral";
  }
  if (res.type == "rare") {
    cssColor = "orange";
    cssTextShadow = "1px 0px 10px Coral";
  }
  if (res.color) {
    cssColor = res.color;
    cssTextShadow = undefined;
  }

  // 存量上限告警（left.jsx.js:126-131）
  let amtClassName = "resAmount";
  if (res.value > (res.maxValue as number) * 0.95 && (res.maxValue as number) > 0) {
    amtClassName = "resAmount resLimitNotice";
  } else if (res.value > (res.maxValue as number) * 0.75 && (res.maxValue as number) > 0) {
    amtClassName = "resAmount resLimitWarn";
  }

  // 季节修正（left.jsx.js:137-156）
  let weatherValue: string | null = null;
  let weatherClass: string | null = null;
  const season = game.calendar.getCurSeason();
  if (season.modifiers[res.name] && perTick !== 0) {
    let modifier = game.calendar.getWeatherMod(res);
    if (modifier == 0) {
      modifier = -100;
    } else {
      modifier = Math.max(Math.round((modifier - 1) * 100), -99);
    }
    weatherValue = modifier ? "[" + (modifier > 0 ? "+" : "") + modifier.toFixed() + "%]" : "";
    if (modifier > 0) {
      weatherClass = "positive-weather";
    } else if (modifier < 0) {
      weatherClass = "negative-weather";
    }
  }

  // 彩蛋 class（left.jsx.js:161-169）
  let specialClass = "";
  if (res.value == 420) {
    specialClass = " blaze";
  } else if (res.value == 666) {
    specialClass = " hail";
  } else if (res.value == 777) {
    specialClass = " pray";
  } else if (res.value == 1337) {
    specialClass = " leet";
  }

  // 领袖加成（left.jsx.js:171-185）
  let leaderBonus = "";
  const currentLeader = game.village.leader;
  if (currentLeader && currentLeader.job) {
    const currentLeaderJob = game.village.getJob(currentLeader.job);
    if (currentLeaderJob) {
      for (const jobResName in currentLeaderJob.modifiers) {
        if (res.name == jobResName) {
          leaderBonus = " resLeaderBonus ";
        }
      }
    }
  }

  // 名称格 title 的百分比段（left.jsx.js:192-198）
  let resPercent: string;
  if (res.maxValue) {
    resPercent = ((res.value / (res.maxValue as number)) * 100).toFixed() + "%/" + game.getDisplayValueExt(res.maxValue);
  } else {
    //Display value with 1 digit of precision
    resPercent = game.getDisplayValueExt(Math.round(res.value), false, false, 1);
  }

  const title = res.title || res.name;
  return {
    shown: true,
    title,
    rowHidden: !res.visible,
    cssColor,
    cssTextShadow,
    styleRef: res.style,
    valueText: game.getDisplayValueExt(res.value),
    amtClassName,
    specialClass,
    maxText: res.maxValue ? "/" + game.getDisplayValueExt(res.maxValue) : "",
    perTickVal,
    weatherValue,
    weatherClass,
    leaderBonus,
    nameTitle: title + " " + resPercent + " " + perTickVal,
    isHidden: !!res.isHidden,
  };
}

// ---------------------------------------------------------------------------
// 组件
// ---------------------------------------------------------------------------

export interface ResourceRowProps {
  /** 资源名（按 name 键控；伪资源对象每次重建，故不传对象） */
  name: string;
  isEditMode: boolean;
  isRequired: boolean;
  showHiddenResources: boolean;
  isTemporalParadox: boolean;
}

export function ResourceRow({
  name,
  isEditMode,
  isRequired,
  showHiddenResources,
  isTemporalParadox,
}: ResourceRowProps) {
  const selector = useCallback(
    (game: GamePage | null) => computeRowSnapshot(game, name, { isEditMode, showHiddenResources }),
    [name, isEditMode, showHiddenResources],
  );
  const snap = useGameTick(selector, snapshotEq);

  const toggleView = useCallback(() => {
    const game = getGame();
    const res = game ? resolveResource(game, name) : null;
    if (game && res) {
      game.resPool.setResourceIsHidden(name, !res.isHidden);
    }
  }, [name]);

  if (!snap || !snap.shown) {
    return null;
  }

  // 合并顺序对齐旧版：type/color 为基底，res.style 的键最后覆盖（left.jsx.js:118-122）
  const nameCss: CSSProperties = {};
  if (snap.cssColor) {
    nameCss.color = snap.cssColor;
  }
  if (snap.cssTextShadow) {
    nameCss.textShadow = snap.cssTextShadow;
  }
  Object.assign(nameCss, snap.styleRef);

  const resRowClass =
    "res-row resource_" + name + snap.leaderBonus +
    (isRequired ? " highlited" : "") +
    (snap.rowHidden ? " hidden" : "");

  return (
    <div role="row" className={resRowClass}>
      {isEditMode && (
        <div className="res-cell">
          <input
            type="checkbox"
            checked={!snap.isHidden}
            title={"Toggle " + t("resources." + name + ".title")}
            onChange={toggleView}
            style={{ display: "inline-block" }}
          />
        </div>
      )}
      <div
        className="res-cell resource-name"
        style={nameCss}
        onClick={(e: MouseEvent) => {
          if (isEditMode || e.ctrlKey || e.metaKey) {
            toggleView();
          }
        }}
        title={snap.nameTitle}
        role="gridcell"
        tabIndex={0}
      >
        {snap.title}
      </div>
      <div className={"res-cell " + snap.amtClassName + snap.specialClass} role="gridcell">
        {snap.valueText}
      </div>
      <div className="res-cell maxRes" role="gridcell">
        {snap.maxText}
      </div>
      <TooltipAnchor
        className="res-cell resPerTick"
        role="gridcell"
        activeStyle={{ fontWeight: "bold" }}
        getTooltip={() => {
          // 内容在调用时重新解析资源（伪资源对象每次 tick 重建），
          // 门控与正文逐字对齐旧 attachResourceTooltip/getDetailedResMap
          const game = getGame();
          const res = game ? resolveResource(game, name) : null;
          return game && res ? getResourceTooltipContent(game, res) : "";
        }}
      >
        {isTemporalParadox ? "???" : snap.perTickVal}
      </TooltipAnchor>
      <div className={"res-cell" + (snap.weatherClass ? " " + snap.weatherClass : "")} role="gridcell">
        {snap.weatherValue}
      </div>
    </div>
  );
}
